import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useSettings } from './SettingsContext';
import { isAppLockActive } from '../utils/securitySettings';
import SessionManager from '../services/SessionManager';
import { SESSION_CONFIG } from '../config/sessionConfig';
import { ensureFirebaseInitialized } from '../utils/firebaseInitialization';

interface AppLockContextType {
  isAppLocked: boolean;
  shouldShowAppLock: boolean;
  unlockApp: () => void;
  lockApp: () => void;
  checkAppLockStatus: () => Promise<void>;
}

const AppLockContext = createContext<AppLockContextType | undefined>(undefined);

export const useAppLock = () => {
  const context = useContext(AppLockContext);
  if (!context) {
    throw new Error('useAppLock must be used within an AppLockProvider');
  }
  return context;
};

interface AppLockProviderProps {
  children: ReactNode;
}

export const AppLockProvider = ({ children }: AppLockProviderProps) => {
  const [isAppLocked, setIsAppLocked] = useState(false);
  const [shouldShowAppLock, setShouldShowAppLock] = useState(false);
  const [backgroundTimestamp, setBackgroundTimestamp] = useState<number | null>(null);
  const [isMetroAvailable, setIsMetroAvailable] = useState(true);
  const [isAppLockDisabled, setIsAppLockDisabled] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const { settings } = useSettings();
  const sessionManager = SessionManager.getInstance();
  
  // Add refs to prevent race conditions
  const isCheckingRef = useRef(false);
  const isLockingRef = useRef(false);
  const lastCheckTimeRef = useRef(0);
  const recoveryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasBackgroundedRef = useRef(false);
  const unlockGraceUntilRef = useRef(0);
  const shouldShowAppLockRef = useRef(false);
  const isAppLockedRef = useRef(false);

  console.log('🔐 AppLockProvider: HOT RELOAD TEST - TIME:', new Date().toLocaleTimeString());

  // App Lock (PIN) and/or Fingerprint Unlock can each trigger the lock screen.
  const isAppLockEnabled = settings?.security?.appLock || false;
  const isBiometricEnabled = settings?.security?.biometric || false;
  const isLocalLockEnabled = isAppLockActive(
    settings?.security ?? { appLock: false, biometric: false }
  );

  useEffect(() => {
    shouldShowAppLockRef.current = shouldShowAppLock;
    isAppLockedRef.current = isAppLocked;
  }, [shouldShowAppLock, isAppLocked]);

  console.log('AppLockProvider: Rendering with settings:', {
    isAppLockEnabled,
    isBiometricEnabled,
    isAppLocked,
    shouldShowAppLock,
    isMetroAvailable,
    isAppLockDisabled,
    isRecovering
  });

  useEffect(() => {
    sessionManager.updateSessionConfig({
      isAppLockEnabled,
      isBiometricEnabled,
      timeoutDuration: 5 * 60 * 1000, // 5 minutes for Firebase session
    });
  }, [isAppLockEnabled, isBiometricEnabled, sessionManager]);

  // If JS is running, Metro/dev bundle is available — avoid false recovery on cold start.
  const checkMetroAvailability = useCallback(() => {
    setIsMetroAvailable(true);
    setIsAppLockDisabled(false);
    setIsRecovering(false);
    return true;
  }, []);

  // Platform-specific app state handling functions
  const handleAppGoingToBackground = useCallback(() => {
    console.log('AppLockProvider: App going to background, app lock enabled:', isAppLockEnabled);
    
    // Clear any recovery timeouts
    if (recoveryTimeoutRef.current) {
      clearTimeout(recoveryTimeoutRef.current);
      recoveryTimeoutRef.current = null;
    }
    
    if (isLocalLockEnabled) {
      setBackgroundTimestamp(Date.now());
      console.log('AppLockProvider: Background timestamp set');
    }
  }, [isLocalLockEnabled]);

  const handleAppComingToForeground = useCallback(async () => {
    console.log('AppLockProvider: App coming to foreground');
    console.log('AppLockProvider: Settings check:', { isAppLockEnabled, isBiometricEnabled });

    if (sessionManager.isAuthFlowSuspended()) {
      console.log('AppLockProvider: OAuth in progress — skipping app lock');
      return;
    }

    if (shouldShowAppLockRef.current || isAppLockedRef.current) {
      console.log('AppLockProvider: Lock screen already showing, skipping');
      return;
    }

    if (Date.now() < unlockGraceUntilRef.current) {
      console.log('AppLockProvider: Unlock grace period active, skipping');
      wasBackgroundedRef.current = false;
      return;
    }

    if (!wasBackgroundedRef.current) {
      console.log('AppLockProvider: App was not backgrounded, skipping lock');
      return;
    }
    
    // Check Metro availability first
    const metroAvailable = checkMetroAvailability();
    
    if (!isLocalLockEnabled) {
      console.log('AppLockProvider: No app lock configured');
      wasBackgroundedRef.current = false;
      return;
    }

    // If app lock is disabled due to Metro issues, completely skip
    if (isAppLockDisabled || isRecovering) {
      console.log('AppLockProvider: App lock disabled or recovering, skipping');
      // Update last active time to prevent future issues
      try {
        await AsyncStorage.setItem('lastActiveTime', Date.now().toString());
      } catch (error) {
        console.error('AppLockProvider: Error updating last active time:', error);
      }
      return;
    }

    // Prevent multiple simultaneous checks
    if (isCheckingRef.current || isLockingRef.current) {
      console.log('AppLockProvider: Already checking or locking, skipping');
      return;
    }

    // Debounce rapid calls
    const now = Date.now();
    if (now - lastCheckTimeRef.current < 1000) {
      console.log('AppLockProvider: Debouncing rapid calls');
      return;
    }
    lastCheckTimeRef.current = now;

    isCheckingRef.current = true;

    try {
      // Show app lock immediately when app comes to foreground (removed session timeout)
      console.log('AppLockProvider: App lock is enabled, showing lock screen');
      isLockingRef.current = true;
      setIsAppLocked(true);
      setShouldShowAppLock(true);
      setBackgroundTimestamp(null);
      wasBackgroundedRef.current = false;
    } catch (error) {
      console.error('AppLockProvider: Error showing app lock:', error);
      // On error, don't show lock screen to prevent crashes
      // Instead, just update the last active time to prevent future issues
      try {
        await AsyncStorage.setItem('lastActiveTime', Date.now().toString());
      } catch (storageError) {
        console.error('AppLockProvider: Error updating last active time:', storageError);
      }
    } finally {
      isCheckingRef.current = false;
      isLockingRef.current = false;
    }
  }, [isLocalLockEnabled, checkMetroAvailability, isAppLockDisabled, isRecovering, sessionManager]);

  // Platform-specific app state handling
  const handleAppStateChange = useCallback((nextAppState: AppStateStatus) => {
    console.log('AppLockProvider: App state changed to:', nextAppState);
    
    if (nextAppState === 'background') {
      wasBackgroundedRef.current = true;
      handleAppGoingToBackground();
    } else if (nextAppState === 'active') {
      setTimeout(() => {
        handleAppComingToForeground();
      }, 300);
    }
  }, [handleAppGoingToBackground, handleAppComingToForeground]);

  // Cross-platform app state listener
  useEffect(() => {
    let subscription: any;
    
    console.log('AppLockProvider: Initializing for platform:', Platform.OS);
    
    if (Platform.OS !== 'web') {
      // Native platforms use AppState
      try {
        subscription = AppState.addEventListener('change', handleAppStateChange);
        console.log('AppLockProvider: Native app state listener initialized successfully');
      } catch (error) {
        console.error('AppLockProvider: Error initializing native app state listener:', error);
      }
    } else {
      // Web platform uses visibility API
      try {
        const globalWindow = (global as any).window || (globalThis as any).window;
        const globalDocument = (global as any).document || (globalThis as any).document;
        
        if (globalWindow && globalDocument) {
          const handleVisibilityChange = () => {
            if (globalDocument.hidden) {
              console.log('AppLockProvider: Web page hidden');
              handleAppGoingToBackground();
            } else {
              console.log('AppLockProvider: Web page visible');
              handleAppComingToForeground();
            }
          };

          const handleFocus = () => {
            console.log('AppLockProvider: Web page focused');
            handleAppComingToForeground();
          };

          const handleBlur = () => {
            console.log('AppLockProvider: Web page blurred');
            handleAppGoingToBackground();
          };

          // Add event listeners
          globalDocument.addEventListener('visibilitychange', handleVisibilityChange);
          globalWindow.addEventListener('focus', handleFocus);
          globalWindow.addEventListener('blur', handleBlur);
          
          console.log('AppLockProvider: Web listeners initialized successfully');
        }
      } catch (error) {
        console.error('AppLockProvider: Web listeners not available:', error);
      }
    }
    
    return () => {
      try {
        if (subscription) {
          subscription.remove();
          console.log('AppLockProvider: App state listeners cleaned up');
        }
        if (recoveryTimeoutRef.current) {
          clearTimeout(recoveryTimeoutRef.current);
          console.log('AppLockProvider: Recovery timeout cleaned up');
        }
      } catch (error) {
        console.error('AppLockProvider: Error cleaning up listeners:', error);
      }
    };
  }, [handleAppStateChange]);

  const unlockApp = async () => {
    console.log('AppLockProvider: Unlocking app - START', {
      isAppLocked,
      shouldShowAppLock,
      backgroundTimestamp
    });

    unlockGraceUntilRef.current = Date.now() + 2500;
    wasBackgroundedRef.current = false;
    
    setIsAppLocked(false);
    setShouldShowAppLock(false);
    setBackgroundTimestamp(null);
    
    // Update last active time immediately
    try {
      await AsyncStorage.setItem('lastActiveTime', Date.now().toString());
      console.log('AppLockProvider: Updated last active time');
    } catch (error) {
      console.error('AppLockProvider: Error updating last active time:', error);
    }
    
    console.log('AppLockProvider: Unlocking app - END');
  };

  const lockApp = async () => {
    console.log('AppLockProvider: Locking app');
    setIsAppLocked(true);
    setShouldShowAppLock(true);
  };

  const checkAppLockStatus = async () => {
    console.log('AppLockProvider: checkAppLockStatus called');
    console.log('AppLockProvider: Settings:', { isAppLockEnabled, isBiometricEnabled });
    
    if (!isLocalLockEnabled) {
      console.log('AppLockProvider: App lock not enabled');
      return;
    }

    // If app lock is disabled due to Metro issues, completely skip
    if (isAppLockDisabled || isRecovering) {
      console.log('AppLockProvider: App lock disabled or recovering, completely skipping app lock check');
      // Update last active time to prevent future issues
      try {
        await AsyncStorage.setItem('lastActiveTime', Date.now().toString());
      } catch (error) {
        console.error('AppLockProvider: Error updating last active time:', error);
      }
      return;
    }

    // Prevent multiple simultaneous checks
    if (isCheckingRef.current || isLockingRef.current) {
      console.log('AppLockProvider: Already checking or locking, skipping');
      return;
    }

    isCheckingRef.current = true;

    try {
      // Show app lock immediately (removed session timeout)
      console.log('AppLockProvider: App lock enabled, showing lock screen');
      isLockingRef.current = true;
      setIsAppLocked(true);
      setShouldShowAppLock(true);
    } catch (error) {
      console.error('AppLockProvider: Error checking session:', error);
      // On error, don't show lock screen to prevent crashes
    } finally {
      isCheckingRef.current = false;
      isLockingRef.current = false;
    }
  };

  // Save last active time when app becomes active
  useEffect(() => {
    if (!isAppLocked && isLocalLockEnabled) {
      AsyncStorage.setItem('lastActiveTime', Date.now().toString()).catch(console.error);
    }
  }, [isAppLocked, isLocalLockEnabled]);

  const value = {
    isAppLocked,
    shouldShowAppLock,
    unlockApp,
    lockApp,
    checkAppLockStatus,
  };

  // Wrap the provider in a try-catch to prevent crashes
  try {
    return (
      <AppLockContext.Provider value={value}>
        {children}
      </AppLockContext.Provider>
    );
  } catch (error) {
    console.error('AppLockProvider: Critical error in render:', error);
    // Return a minimal provider to prevent crashes
    return (
      <AppLockContext.Provider value={{
        isAppLocked: false,
        shouldShowAppLock: false,
        unlockApp: () => {},
        lockApp: () => {},
        checkAppLockStatus: async () => {},
      }}>
        {children}
      </AppLockContext.Provider>
    );
  }
};
